// Shared Markdown helpers (plan §P1.9 — UI Workflow Consistency Audit).
//
// Three components (CommandCenterPage, ContentAwareG1Gate,
// ContentAwareG2Gate) carried their own private versions of these
// helpers — sometimes with subtly different regexes. Consolidating
// them here means a single source of truth for "strip the YAML
// frontmatter" / "first paragraph" / "extract goals" so every surface
// renders identical excerpts from the same artifact.

/**
 * Strip a leading YAML frontmatter block (between two ``---`` lines).
 *
 * Tolerates both:
 *   * the bare form (``---\nfoo: bar\n---\n…body…``)
 *   * the fenced form some Phase-1 runs emit
 *     (```` ```yaml\n---\nfoo: bar\n---\n``` `````)
 *
 * Returns the body text with leading whitespace trimmed off so the
 * caller can prepend its own headings without a stray blank line.
 */
export function stripFrontmatter(md: string): string {
  if (!md) return ''
  let out = md
  const fenced = out.match(/^```yaml\s*\n---[\s\S]*?---\s*\n```\s*/m)
  if (fenced) out = out.slice(fenced[0].length)
  out = out.replace(/^---[\s\S]*?---\s*/m, '')
  return out
}

/**
 * Pull the first non-heading, non-fence paragraph out of a Markdown
 * blob. Used for the Inspector's "Project charter · excerpt" tile and
 * any other "show the user a one-glance preview" surface.
 */
export function firstParagraph(md: string): string {
  const paras = md.split(/\n\n/).map((p) => p.trim()).filter(Boolean)
  for (const p of paras) {
    if (p.startsWith('#')) continue
    if (p.startsWith('```')) continue
    return p.replace(/\s+/g, ' ')
  }
  return ''
}

/**
 * Walk the bullet/asterisk list under a ``Goal Hierarchy`` heading in
 * an intent-map and return the items as ``{ depth, text }``. Allows
 * numbered headings ("# 2. Goal Hierarchy") because that's how the
 * Phase-1 Contextualist tends to format the doc.
 *
 * The ``maxRows`` cap is conservative — the Inspector only shows the
 * first 8 entries and the G1 panel the first 12, so capping at 15 is
 * plenty.
 */
export function extractGoals(
  md: string,
  maxRows = 15,
): Array<{ depth: number; text: string }> {
  const body = stripFrontmatter(md)
  const rows: Array<{ depth: number; text: string }> = []
  const section = body.match(
    /#+\s*(?:\d+[.)]\s*)?Goal\s+Hierarchy[^\n]*\n+([\s\S]*?)(?:\n#+|\n---|$)/i,
  )
  const src = section ? section[1] : body
  for (const line of src.split('\n')) {
    const m = line.match(/^(\s*)[-*•]\s+(.*)$/)
    if (m) {
      const depth = Math.floor(m[1].length / 2)
      const text = m[2].replace(/`/g, '').trim()
      if (text) rows.push({ depth, text })
    }
    if (rows.length >= maxRows) break
  }
  return rows
}

// Backwards-compat alias used by older imports inside CommandCenterPage.
export const stripFm = stripFrontmatter
